"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import dynamic from "next/dynamic";
import { PART_BY_ID } from "@/lib/game/parts";

const PixiPartThumbnail = dynamic(
  () => import("@/components/PixiPartThumbnail").then((m) => m.PixiPartThumbnail),
  { ssr: false },
);

type RewardKind = "part" | "stars";

interface RewardItem {
  id: string;
  kind: RewardKind;
  partId?: string;
  stars?: number;
}

interface RewardNotificationsContextValue {
  showPartCollect: (partId: string) => void;
  showStarCollect: (stars: number) => void;
}

const RewardNotificationsContext = createContext<RewardNotificationsContextValue | null>(null);

function RewardCollectCard({
  item,
  bagX,
  bagY,
  onDone,
}: {
  item: RewardItem;
  bagX: number;
  bagY: number;
  onDone: () => void;
}) {
  const [phase, setPhase] = useState<"pop" | "fly">("pop");
  const part = item.partId ? PART_BY_ID[item.partId] : undefined;

  useEffect(() => {
    const popTimer = window.setTimeout(() => setPhase("fly"), 900);
    const flyTimer = window.setTimeout(onDone, 1650);
    return () => {
      window.clearTimeout(popTimer);
      window.clearTimeout(flyTimer);
    };
  }, [onDone]);

  const flyStyle: CSSProperties | undefined =
    phase === "fly"
      ? {
          ["--bag-x" as string]: `${bagX}px`,
          ["--bag-y" as string]: `${bagY}px`,
        }
      : undefined;

  return (
    <div
      className={`reward-collect-card pointer-events-none fixed left-1/2 top-1/2 z-[120] flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-3 ${
        phase === "pop" ? "reward-collect-pop" : "reward-collect-fly"
      }`}
      style={flyStyle}
      role="status"
      aria-live="polite"
    >
      <div className="flex h-28 w-28 items-center justify-center rounded-3xl border-4 border-white/80 bg-gradient-to-br from-violet-500 to-indigo-700 shadow-[0_12px_40px_rgba(0,0,0,0.45)] sm:h-32 sm:w-32">
        {item.kind === "part" && item.partId ? (
          <PixiPartThumbnail partId={item.partId} className="h-24 w-24 sm:h-28 sm:w-28" />
        ) : (
          <span className="text-5xl sm:text-6xl" aria-hidden>
            ⭐
          </span>
        )}
      </div>
      <div className="max-w-[min(90vw,20rem)] rounded-2xl border-2 border-white/70 bg-slate-950/90 px-5 py-3 text-center shadow-xl">
        {item.kind === "part" ? (
          <>
            <p className="text-xs font-bold uppercase tracking-wide text-violet-300">New part!</p>
            <p className="text-xl font-extrabold text-white sm:text-2xl">{part?.name ?? "Part"}</p>
          </>
        ) : (
          <>
            <p className="text-xs font-bold uppercase tracking-wide text-amber-300">Stars earned!</p>
            <p className="text-3xl font-black text-amber-200 sm:text-4xl">+{item.stars} ⭐</p>
          </>
        )}
      </div>
    </div>
  );
}

export function RewardNotificationsProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<RewardItem[]>([]);
  const [active, setActive] = useState<RewardItem | null>(null);
  const bagRef = useRef<HTMLDivElement>(null);
  const [bagOffset, setBagOffset] = useState({ x: 0, y: 0 });

  const showPartCollect = useCallback((partId: string) => {
    setQueue((q) => [...q, { id: crypto.randomUUID(), kind: "part", partId }]);
  }, []);

  const showStarCollect = useCallback((stars: number) => {
    if (stars <= 0) return;
    setQueue((q) => [...q, { id: crypto.randomUUID(), kind: "stars", stars }]);
  }, []);

  useEffect(() => {
    if (active || queue.length === 0) return;
    setActive(queue[0]!);
    setQueue((q) => q.slice(1));
  }, [active, queue]);

  useEffect(() => {
    const measure = () => {
      const bag = bagRef.current;
      if (!bag) return;
      const rect = bag.getBoundingClientRect();
      const centerX = window.innerWidth / 2;
      const centerY = window.innerHeight / 2;
      setBagOffset({
        x: rect.left + rect.width / 2 - centerX,
        y: rect.top + rect.height / 2 - centerY,
      });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [active]);

  const value = useMemo(
    () => ({ showPartCollect, showStarCollect }),
    [showPartCollect, showStarCollect],
  );

  return (
    <RewardNotificationsContext.Provider value={value}>
      {children}
      <div
        ref={bagRef}
        className="pointer-events-none fixed top-3 right-3 z-[110] flex h-12 w-12 items-center justify-center rounded-2xl border-2 border-white/40 bg-slate-900/80 text-2xl shadow-lg backdrop-blur-sm sm:top-4 sm:right-4 sm:h-14 sm:w-14"
        aria-hidden
      >
        🎒
      </div>
      {active && (
        <RewardCollectCard
          item={active}
          bagX={bagOffset.x}
          bagY={bagOffset.y}
          onDone={() => setActive(null)}
        />
      )}
    </RewardNotificationsContext.Provider>
  );
}

export function useRewardNotifications() {
  const ctx = useContext(RewardNotificationsContext);
  if (!ctx) {
    throw new Error("useRewardNotifications must be used within RewardNotificationsProvider");
  }
  return ctx;
}
